/**
 * MiniPopupNotification - Slide-in Notification Popup
 *
 * Popup แจ้งเตือนเมื่อมีเคสซ่อมบำรุงใหม่
 * - Slide-in animation จากขวา
 * - แสดงข้อมูลสรุป: Device, Issue Type, Time
 * - Quick action buttons: ดูรายละเอียด, ปิด
 * - Auto-hide ตาม settings.popupDuration (หรือ autoHideDuration prop ถ้าระบุ)
 * - Progress bar แสดงเวลาที่เหลือ
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useMaintenanceAlert, MaintenanceAlert } from '../../contexts/MaintenanceAlertContext';
import { useNavigate } from 'react-router-dom';
import settingsService from '../../services/settingsService';
import { usePermission } from '../../hooks/usePermission';

// =============== Types ===============

interface MiniPopupNotificationProps {
  className?: string;
  autoHideDuration?: number; // milliseconds
  position?: 'top-right' | 'bottom-right' | 'top-left' | 'bottom-left';
}

// =============== Helper Components ===============

interface ProgressBarProps {
  duration: number;
  isPaused: boolean;
  onComplete: () => void;
}

const ProgressBar: React.FC<ProgressBarProps> = ({ duration, isPaused, onComplete }) => {
  const [progress, setProgress] = useState(100);
  // BUG-S2284-001: เวลาที่เหลือต้องต่อจากตอนหยุด (hover) — เดิมทุกครั้งที่เลิก hover เริ่มนับใหม่เต็ม duration
  const remainingRef = useRef(duration);

  useEffect(() => {
    if (isPaused) return;

    // performance.now() เป็นนาฬิกาเดินหน้าเสมอ ไม่กระโดดตามการตั้งเวลาเครื่อง (Date.now() กระโดดได้)
    const endTime = performance.now() + remainingRef.current;
    let animationId = 0;

    const updateProgress = () => {
      const remaining = endTime - performance.now();
      remainingRef.current = Math.max(remaining, 0);
      const newProgress = (remaining / duration) * 100;

      if (newProgress <= 0) {
        setProgress(0);
        onComplete();
      } else {
        setProgress(newProgress);
        animationId = requestAnimationFrame(updateProgress);
      }
    };

    animationId = requestAnimationFrame(updateProgress);

    return () => cancelAnimationFrame(animationId);
  }, [duration, isPaused, onComplete]);

  return (
    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-200 overflow-hidden">
      <div
        className="h-full bg-gradient-to-r from-red-500 to-orange-500 transition-all duration-100 ease-linear"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
};

// =============== Popup Item ===============

interface PopupItemProps {
  alert: MaintenanceAlert;
  onView: () => void;
  onClose: () => void;
  autoHideDuration: number;
  canView: boolean;
}

const PopupItem: React.FC<PopupItemProps> = ({
  alert,
  onView,
  onClose,
  autoHideDuration,
  canView,
}) => {
  const [isPaused, setIsPaused] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  // FINDING-S1071-A wire: consume displaySettings.timeFormat (12 or 24)
  // - "12" → hour12: true (e.g., "2:30 PM")
  // - "24" หรือค่าอื่น → hour12: false (e.g., "14:30") fail-open default
  // - Fail-open: fetch error → use 24h (safer canonical default)
  const [hour12, setHour12] = useState<boolean>(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const settings = await settingsService.getGeneralSettings();
        const raw = settings?.displaySettings?.timeFormat;
        if (cancelled) return;
        // Only "12" turns on hour12; any other value (incl. "24" / "24h" / invalid) → 24h format
        setHour12(String(raw).trim() === '12');
      } catch {
        // Fail-open: keep 24h default
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const priorityStyles = {
    urgent: {
      border: 'border-l-red-600',
      bg: 'bg-gradient-to-r from-red-50 to-white',
      badge: 'bg-red-100 text-red-700 ring-1 ring-red-200',
      icon: 'text-red-600',
    },
    high: {
      border: 'border-l-orange-500',
      bg: 'bg-gradient-to-r from-orange-50 to-white',
      badge: 'bg-orange-100 text-orange-700 ring-1 ring-orange-200',
      icon: 'text-orange-600',
    },
    normal: {
      border: 'border-l-blue-500',
      bg: 'bg-gradient-to-r from-blue-50 to-white',
      badge: 'bg-blue-100 text-blue-700 ring-1 ring-blue-200',
      icon: 'text-blue-600',
    },
    low: {
      border: 'border-l-gray-400',
      bg: 'bg-gradient-to-r from-gray-50 to-white',
      badge: 'bg-gray-100 text-gray-600 ring-1 ring-gray-200',
      icon: 'text-gray-500',
    },
  };

  const priorityLabels = {
    urgent: 'เร่งด่วน',
    high: 'สำคัญ',
    normal: 'ปกติ',
    low: 'ต่ำ',
  };

  const styles = priorityStyles[alert.priority?.toLowerCase() as keyof typeof priorityStyles] || priorityStyles.normal;

  // E7: ทุกเคสได้ priority = NORMAL เสมอ เพราะยังไม่มีหน้าจอไหนให้กรอก/แก้ความเร่งด่วน
  // ป้าย "ปกติ" ที่ขึ้นทุกใบจึงไม่ได้บอกอะไร และผู้ใช้อ่านแล้วเหมือนระบบเสีย
  // → ซ่อนป้ายเมื่อเป็นค่า default แล้วโชว์เฉพาะตอนที่ "ผิดปกติจริง" (สูง/ด่วนมาก/ต่ำ)
  // ถ้าวันหนึ่งต่อ UI ให้เลือกความเร่งด่วนได้ ป้าย "ปกติ" จะกลับมามีความหมายและเอาเงื่อนไขนี้ออกได้
  const priorityKey = alert.priority?.toLowerCase();
  const showPriorityBadge = !!priorityKey && priorityKey !== 'normal';

  const handleClose = useCallback(() => {
    setIsExiting(true);
    setTimeout(onClose, 300); // Wait for exit animation
  }, [onClose]);

  const handleAutoHide = useCallback(() => {
    if (!isPaused) {
      handleClose();
    }
  }, [isPaused, handleClose]);

  const formatTime = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      // FINDING-S1071-A: pass hour12 จาก setting (12h vs 24h)
      return date.toLocaleTimeString('th-TH', {
        hour: '2-digit',
        minute: '2-digit',
        hour12,
      });
    } catch {
      return '';
    }
  };

  return (
    <div
      className={`relative w-96 max-w-[calc(100vw-2rem)] bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden transform transition-all duration-300 ${
        isExiting ? 'translate-x-full opacity-0' : 'translate-x-0 opacity-100'
      }`}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      role="alert"
      aria-live="assertive"
    >
      {/* Main Content */}
      <div className={`border-l-4 ${styles.border} ${styles.bg}`}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 pt-3 pb-2">
          <div className="flex items-center gap-2">
            {/* Animated Icon */}
            <div className={`relative ${styles.icon}`}>
              <svg
                className="w-5 h-5 animate-pulse"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                />
              </svg>
              {/* Pulse ring */}
              <span className="absolute inset-0 animate-ping rounded-full bg-current opacity-20" />
            </div>
            <span className="font-semibold text-gray-800 text-sm">
              เคสซ่อมบำรุงใหม่
            </span>
            {showPriorityBadge && (
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${styles.badge}`}>
                {priorityLabels[priorityKey as keyof typeof priorityLabels] || alert.priority}
              </span>
            )}
          </div>
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
            aria-label="ปิด"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-4 pb-3">
          {/* Device Info */}
          <div className="flex items-center gap-2 mb-2">
            <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"
              />
            </svg>
            <span className="font-medium text-gray-900">{alert.deviceCode}</span>
          </div>

          {/* Issue Type */}
          <p className="text-sm text-gray-600 mb-1 truncate">
            ประเภท: {alert.issueType}
          </p>

          {/* Site Name */}
          {alert.siteName && (
            <p className="text-sm text-gray-500 mb-1 truncate flex items-center gap-1">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              {alert.siteName}
            </p>
          )}

          {/* Time */}
          <p className="text-xs text-gray-400 flex items-center gap-1">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            {formatTime(alert.timestamp)}
          </p>
        </div>

        {/* Actions */}
        {/* G-13/D4: ซ่อน "ดูรายละเอียด" เมื่อไม่มี maintenanceId จริง (testPopup ส่ง 0)
            หรือผู้ใช้ไม่มีสิทธิ์ maintenance.view — กันนำทางไป /maintenance/view/0 หรือหน้า access denied */}
        <div className="flex items-center gap-2 px-4 pb-3">
          {canView && (
            <button
              onClick={() => {
                onView();
                handleClose();
              }}
              className="flex-1 px-3 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors shadow-sm"
            >
              ดูรายละเอียด
            </button>
          )}
          <button
            onClick={handleClose}
            className={`${canView ? '' : 'flex-1 '}px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors`}
          >
            ปิด
          </button>
        </div>
      </div>

      {/* Progress Bar */}
      <ProgressBar
        duration={autoHideDuration}
        isPaused={isPaused}
        onComplete={handleAutoHide}
      />
    </div>
  );
};

// =============== Main Component ===============

const MiniPopupNotification: React.FC<MiniPopupNotificationProps> = ({
  className = '',
  autoHideDuration,
  position = 'top-right',
}) => {
  const { latestAlert, clearLatestAlert, settings } = useMaintenanceAlert();
  const { hasPermission } = usePermission();

  // Use settings.popupDuration if autoHideDuration prop not provided
  const effectiveDuration = autoHideDuration ?? settings.popupDuration;
  const navigate = useNavigate();

  // G-13/D4: CTA ใช้ได้เฉพาะเมื่อมี maintenanceId จริง (testPopup fabricate เป็น 0)
  // และมีสิทธิ์ maintenance.view (route /maintenance/view/:id gate ด้วยสิทธิ์นี้ใน App.tsx)
  const canViewDetail = Boolean(latestAlert?.maintenanceId) && hasPermission('maintenance.view');

  const handleView = useCallback(() => {
    if (latestAlert) {
      navigate(`/maintenance/view/${latestAlert.maintenanceId}`);
    }
  }, [latestAlert, navigate]);

  const handleClose = useCallback(() => {
    clearLatestAlert();
  }, [clearLatestAlert]);

  // Position classes
  const positionClasses = {
    'top-right': 'top-4 right-4',
    'bottom-right': 'bottom-4 right-4',
    'top-left': 'top-4 left-4',
    'bottom-left': 'bottom-4 left-4',
  };

  if (!latestAlert) {
    return null;
  }

  return (
    <div
      className={`fixed ${positionClasses[position]} z-[9999] animate-[slideInRight_0.3s_ease-out] ${className}`}
    >
      <PopupItem
        alert={latestAlert}
        onView={handleView}
        onClose={handleClose}
        autoHideDuration={effectiveDuration}
        canView={canViewDetail}
      />
    </div>
  );
};

export default MiniPopupNotification;
